import { ReactNode, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../authProvider";

interface TermsGuardProps {
  children: ReactNode;
}

/**
 * Guard component that checks if user needs to accept Terms and Conditions.
 * If user needs to accept terms, redirects to /accept-terms.
 * Only applies to client-related users (CLIENT, COPYWRITER_CLIENT).
 */
export const TermsGuard = ({ children }: TermsGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const needsTermsAcceptance = useCallback(() => {
    if (!user) return false;
    // Only client-related users need to accept terms
    if (!user.isClientRelated) return false;
    return user.needs_terms_acceptance === true;
  }, [user]);

  useEffect(() => {
    // Don't redirect if already on the accept-terms page
    if (location.pathname === "/accept-terms") {
      return;
    }

    if (needsTermsAcceptance()) {
      // Redirect to terms acceptance page with return URL
      navigate(
        `/accept-terms?returnUrl=${encodeURIComponent(location.pathname)}`
      );
    }
  }, [user, location.pathname, navigate, needsTermsAcceptance]);

  // If user needs to accept terms, don't render children (will redirect)
  if (needsTermsAcceptance() && location.pathname !== "/accept-terms") {
    return null;
  }

  return <>{children}</>;
};
